/**
 * Login gov.br do prescritor para obter numeração SNCR. O link da ponte serve
 * no computador (popup) ou no celular (QR); o backend recebe a numeração e esta
 * janela fecha sozinha quando o pedido termina.
 */
import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Divider,
  IconButton,
  InputBase,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';
import { CLOUD_API_THEME, cloudApiDialogPaperSx, cloudApiDialogPrimaryButtonSx } from '../../pages/whatsappCloudApiTheme';
import {
  SNCR_GOVBR_POPUP_NAME,
  SncrFlowCancelled,
  startSncrGovBrRequest,
  waitForSncrAuthRequest,
  type SncrAuthRequestStatus,
  type SncrGovBrRequest,
  type SncrRequestedItem,
} from '../../services/sncrService';

interface Props {
  open: boolean;
  professionalId: string;
  requested: SncrRequestedItem[];
  onClose: () => void;
  /** Pedido terminou (completed/partial/failed/expired). */
  onFinished: (status: SncrAuthRequestStatus) => void;
}

const SncrGovBrLoginDialog: React.FC<Props> = ({ open, professionalId, requested, onClose, onFinished }) => {
  const [request, setRequest] = useState<SncrGovBrRequest | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [statusText, setStatusText] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const finishedRef = useRef(onFinished);
  finishedRef.current = onFinished;

  const requestedKey = JSON.stringify(requested);

  useEffect(() => {
    if (!open || !professionalId) return undefined;
    const controller = new AbortController();
    setRequest(null);
    setError(null);
    setStatusText(null);
    setCopied(false);
    (async () => {
      try {
        const created = await startSncrGovBrRequest(professionalId, JSON.parse(requestedKey));
        if (controller.signal.aborted) return;
        setRequest(created);
        const status = await waitForSncrAuthRequest({
          requestId: created.id,
          expiresAt: created.expires_at,
          onStatus: setStatusText,
          signal: controller.signal,
        });
        if (!controller.signal.aborted) finishedRef.current(status);
      } catch (err: any) {
        if (controller.signal.aborted || err instanceof SncrFlowCancelled) return;
        setError(err?.message || 'Não foi possível iniciar o login gov.br.');
      }
    })();
    return () => controller.abort();
  }, [open, professionalId, requestedKey, attempt]);

  const openGovBr = () => {
    if (!request) return;
    const popup = window.open(request.url, SNCR_GOVBR_POPUP_NAME, 'width=520,height=720');
    if (!popup) window.open(request.url, '_blank', 'noopener');
  };

  const copyLink = async () => {
    if (!request) return;
    try {
      await navigator.clipboard.writeText(request.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { ...cloudApiDialogPaperSx, borderRadius: 3 } }}>
      <DialogContent sx={{ p: 3.5, position: 'relative', textAlign: 'center' }}>
        <IconButton onClick={onClose} aria-label="Fechar" size="small" sx={{ position: 'absolute', top: 12, right: 12 }}>
          <CloseIcon fontSize="small" />
        </IconButton>

        <Typography sx={{ fontWeight: 700, fontSize: '1.15rem', color: CLOUD_API_THEME.navy, mb: 1.25 }}>
          Entre com sua conta Gov.br
        </Typography>
        <Typography variant="body2" sx={{ color: CLOUD_API_THEME.slate, mb: 2, lineHeight: 1.6 }}>
          Faça login com a conta Gov.br do prescritor para solicitar a numeração da Anvisa. Assim que a
          autenticação for concluída, o processo continuará automaticamente.
        </Typography>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
            textAlign: 'left',
            p: 1.25,
            mb: 2,
            borderRadius: 2,
            border: '1px solid #c7d2fe',
            bgcolor: '#eef2ff',
          }}
        >
          <InfoIcon sx={{ fontSize: 18, color: '#4f46e5', mt: '1px' }} />
          <Typography variant="caption" sx={{ color: '#312e81', lineHeight: 1.5 }}>
            Mantenha esta janela aberta enquanto faz o login. Ela será fechada automaticamente após a autenticação.
          </Typography>
        </Box>

        {error ? (
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: 2, textAlign: 'left' }}
            action={(
              <Button color="inherit" size="small" onClick={() => setAttempt((n) => n + 1)} sx={{ textTransform: 'none' }}>
                Tentar de novo
              </Button>
            )}
          >
            {error}
          </Alert>
        ) : null}

        <Button
          fullWidth
          variant="contained"
          onClick={openGovBr}
          disabled={!request || Boolean(error)}
          sx={{ ...cloudApiDialogPrimaryButtonSx, py: 1.25, mb: 2 }}
        >
          {request ? 'Abrir Gov.br' : <CircularProgress size={20} color="inherit" />}
        </Button>

        <Typography variant="caption" sx={{ color: CLOUD_API_THEME.slate, display: 'block', mb: 0.75 }}>
          Ou copie o link abaixo no seu navegador
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            px: 1.5,
            py: 0.5,
            borderRadius: 2,
            border: `1px solid ${CLOUD_API_THEME.borderSoft}`,
            bgcolor: CLOUD_API_THEME.paper,
          }}
        >
          <InputBase
            value={request?.url || ''}
            readOnly
            placeholder="Gerando link…"
            sx={{ flex: 1, fontSize: '0.75rem', color: CLOUD_API_THEME.slate }}
            inputProps={{ 'aria-label': 'Link de login gov.br' }}
          />
          <Tooltip title={copied ? 'Copiado' : 'Copiar link'}>
            <span>
              <IconButton size="small" onClick={() => void copyLink()} disabled={!request}>
                <CopyIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        <Divider sx={{ my: 2.25 }} />

        <Typography variant="caption" sx={{ color: CLOUD_API_THEME.slate, display: 'block', mb: 1.25 }}>
          Se preferir, faça login pelo celular escaneando o QR Code
        </Typography>
        <Box
          sx={{
            display: 'inline-flex',
            p: 1.75,
            borderRadius: 2,
            border: `1px solid ${CLOUD_API_THEME.borderSoft}`,
            bgcolor: '#fff',
            minWidth: 180,
            minHeight: 180,
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {request ? (
            <QRCodeSVG value={request.url} size={148} level="M" />
          ) : (
            <CircularProgress size={24} sx={{ color: CLOUD_API_THEME.teal }} />
          )}
        </Box>

        {statusText && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mt: 2 }}>
            <CircularProgress size={16} sx={{ color: CLOUD_API_THEME.teal }} />
            <Typography variant="caption" sx={{ color: CLOUD_API_THEME.slate }}>{statusText}</Typography>
          </Box>
        )}
        {request?.environment === 'hmg' && (
          <Typography variant="caption" sx={{ color: '#92400e', display: 'block', mt: 1.5 }}>
            Ambiente de homologação: os números não valem em farmácia.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default SncrGovBrLoginDialog;
